import React, { useContext, useState, useEffect } from "react";
import { useOrganizationContext } from "../../contexts/OrganizationProvider";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { ApolloError } from "apollo-boost";

import {
  pigsQueryComposer,
  PIG_COUNT_SUBSCRIPTION
} from "../../helpers/pigQueryComposer";
import { logErrorToLogdna } from "../../helpers/logdna";
import { useLiveStatusContext } from "../../contexts/LiveStatusProvider";

type PropsType = {
  children: React.ReactNode;
};

export type PigCount = {
  active: number;
  completed: number;
};

export type AlertsCount = {
  activeAlerts: number;
  history: number;
};

interface IPigHeaderContext {
  pigCount: PigCount;
  alertsCount: AlertsCount;
  loading?: boolean;
}
const initialPigContext: IPigHeaderContext = {
  pigCount: {
    active: 0,
    completed: 0
  },
  alertsCount: {
    activeAlerts: 0,
    history: 0
  }
};

type EventType = {
  id: string;
  archived: boolean;
};

const fragment = gql`
  fragment PigsFragment on PaginatedPigsAndTotal {
    active
    completed
  }
`;

// We need an initial event counts for the tabs
const EVENTS_QUERY = gql`
  query events($filters: EventFiltersInput) {
    events(filters: $filters) {
      events {
        id
        archived
        type
      }
    }
  }
`;

// // Make sure your subscriptions match the query structure
// const EVENTS_SUBSCRIPTION = gql`
//   subscription onEventsChange($filters: EventFiltersInput) {
//     eventsUpdated(filters: $filters) {
//       events {
//         id
//         archived
//         type
//       }
//     }
//   }
// `;

export const PigHeaderContext = React.createContext(initialPigContext);
export const usePigHeaderContext = () => useContext(PigHeaderContext);

const PIG_COUNT_QUERY = pigsQueryComposer(fragment);

export const PigHeaderProvider = ({ children }: PropsType) => {
  const [pigCount, setPigCount] = useState<PigCount>(
    initialPigContext.pigCount
  );
  const [alertsCount, setAlertsCount] = useState<AlertsCount>(
    initialPigContext.alertsCount
  );
  const { activeOrg } = useOrganizationContext();
  const { appStatus } = useLiveStatusContext();

  const {
    data: eventData,
    loading: eventLoading,
    error: eventError,
    refetch: refetchEvents
  } = useQuery(EVENTS_QUERY, {
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        pipelines:
          activeOrg && activeOrg.pipelines
            ? activeOrg.pipelines.map(p => p.id)
            : [],
        types: ["pig"]
      }
    }
  });

  // When the appStatus changes we'll re-fetch events to get update counts
  useEffect(() => {
    refetchEvents();
  }, [refetchEvents, appStatus.activePigEventCount]);

  // Updates tab counts when the event counts change.
  useEffect(() => {
    if (
      !eventLoading &&
      !eventError &&
      eventData &&
      eventData.events &&
      eventData.events.events
    ) {
      const activeAlerts = eventData.events.events.filter(
        (e: EventType) => !e.archived
      ).length;
      const history = eventData.events.events.filter(
        (e: EventType) => e.archived
      ).length;
      setAlertsCount({ activeAlerts, history });

      // setActiveCount(
      //   data.events.events.filter((e: EventType) => !e.archived).length
      // );
      // setHistoryCount(
      //   data.events.events.filter((e: EventType) => e.archived).length
      // );
    }
  }, [eventData, eventLoading, eventError]);

  const {
    data: pigData,
    loading: pigLoading,
    subscribeToMore,
    refetch: refetchPigs
  } = useQuery(PIG_COUNT_QUERY, {
    variables: {
      organizationId: activeOrg ? activeOrg.id : undefined
    },
    skip: !activeOrg,
    onError: (err: ApolloError) => {
      logErrorToLogdna(
        "GETTING_ACTIVE_PIGS",
        "Apollo error while getting active pigs.",
        err
      );
      // throw err;
    }
  });

  // const {
  //   data: eventsData,
  //   loading: eventsLoading,
  //   subscribeToMore: eventsSubscribeToMore,
  //   refetch: refetchEvents
  // } = useQuery(EVENTS_QUERY, {
  //   fetchPolicy: "cache-and-network",
  //   variables: {
  //     filters: {
  //       pipelines:
  //         activeOrg && activeOrg.pipelines
  //           ? activeOrg.pipelines.map(p => p.id)
  //           : [],
  //       types: ["pig"]
  //     }
  //   },
  //   skip: !activeOrg,
  //   onError: (err: ApolloError) => {
  //     logErrorToLogdna(
  //       "EVENTS_SUBSCRIPTION_ERROR",
  //       "Apollo error in events subscription.",
  //       err
  //     );
  //     // throw err;
  //   }
  // });

  useEffect(() => {
    if (pigData) {
      const { active, completed } = pigData.pigs;
      setPigCount({ active, completed });
    }
  }, [pigData]);

  // // Update alerts count from events query
  // // Updates tab counts when the event counts change.
  // useEffect(() => {
  //   if (
  //     !eventsLoading &&
  //     eventsData &&
  //     eventsData.events &&
  //     eventsData.events.events
  //   ) {
  //     const activeAlerts = eventsData.events.events.filter(
  //       (e: EventType) => !e.archived
  //     ).length;
  //     const history = eventsData.events.events.filter(
  //       (e: EventType) => e.archived
  //     ).length;
  //     setAlertsCount({ activeAlerts, history });
  //     // console.log("activeAlerts", activeAlerts);
  //     // setAlertsCount(
  //     //   eventsData.events.events.filter((e: EventType) => !e.archived).length
  //     // );
  //     // setHistoryCount(
  //     //   eventsData.events.events.filter((e: EventType) => e.archived).length
  //     // );
  //   }
  // }, [eventsData, eventsLoading]);

  // useEffect(() => {
  //   if (!activeOrg) {
  //     return;
  //   }
  //   try {
  //     eventsSubscribeToMore({
  //       document: EVENTS_SUBSCRIPTION,
  //       variables: {
  //         filters: {
  //           pipelines:
  //             activeOrg && activeOrg.pipelines
  //               ? activeOrg.pipelines.map(p => p.id)
  //               : [],
  //           types: ["pig"]
  //         }
  //       },
  //       updateQuery: (prev, { subscriptionData }) => {
  //         if (!subscriptionData.data) {
  //           return prev;
  //         }
  //         // console.log("")
  //         refetchEvents();
  //         // return subscriptionData.data;
  //       },
  //       onError: (error: Error) => {
  //         logErrorToLogdna(
  //           "EVENTS_SUBSCRIPTION_ERROR",
  //           "Apollo error in events subscription.",
  //           error
  //         );
  //       }
  //     });
  //   } catch (err) {
  //     console.log("PIG_COUNT_SUBSCRIPTION_SETUP_ERROR", err)
  //     logErrorToLogdna(
  //       "PIG_COUNT_SUBSCRIPTION_SETUP_ERROR",
  //       "Failed to setup pig count subscription.",
  //       err
  //     );
  //   }
  //   //suppressing hook dependency because `subscribeToMore` changes on every render
  //   // and the documentation suggest to only call it on component mount
  //   // https://www.apollographql.com/docs/react/data/subscriptions/#subscribing-to-updates-for-a-query
  //   //eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [activeOrg]);

  useEffect(() => {
    if (!activeOrg) {
      return;
    }
    subscribeToMore({
      document: PIG_COUNT_SUBSCRIPTION,
      variables: {
        organizationId: activeOrg.id
      },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) {
          return prev;
        }
        refetchPigs();
        // return subscriptionData.data;
      },
      onError: (error: Error) => {
        logErrorToLogdna(
          "PIG_COUNT_SUBSCRIPTION_ERROR",
          "Apollo error in pig count subscription.",
          error
        );
      }
    });
    //suppressing hook dependency because `subscribeToMore` changes on every render
    // and the documentation suggest to only call it on component mount
    // https://www.apollographql.com/docs/react/data/subscriptions/#subscribing-to-updates-for-a-query
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg]);

  return (
    <PigHeaderContext.Provider
      value={{
        pigCount,
        loading: pigLoading || !activeOrg,
        alertsCount
      }}
    >
      {children}
    </PigHeaderContext.Provider>
  );
};

export default PigHeaderProvider;
